import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline'
import { Badge, Button, Card, CardHeader, InlineSpinner } from '@/components/ui'
import { apiClient, lmsApi } from '@/api'
import { apiErrorMessage, saveBlob } from '@/api/lms'
import type { AdminCourse } from '@/api/lms'

type RowStatus = 'new' | 'existing' | 'update' | 'error'

interface ImportRow {
  row: number
  email: string
  first_name: string
  last_name: string
  status: RowStatus
  errors: string[]
  warnings: string[]
}

interface ImportResult {
  summary: Record<RowStatus | 'total', number>
  columns: string[]
  rows: ImportRow[]
  imported: boolean
  created?: number
  enrollments?: number
  credentials?: { email: string; first_name: string; last_name: string; password: string }[]
  credentials_xlsx?: string | null
}

const COLUMNS = [
  ['Email', 'Obbligatoria: è il nome utente per accedere'],
  ['Nome', 'Obbligatorio'],
  ['Cognome', 'Obbligatorio'],
  ['Codice fiscale', "Necessario per l'attestato"],
  ['Data di nascita', "gg/mm/aaaa, necessaria per l'attestato"],
  ['Luogo di nascita', "Necessario per l'attestato"],
  ['Azienda o scuola', 'Facoltativa'],
  ['Qualifica o classe', 'Facoltativa'],
  ['Password', 'Facoltativa: se vuota viene generata (almeno 8 caratteri)'],
]

const ROLES = [
  { value: 'member', label: 'Membro' },
  { value: 'guest', label: 'Ospite' },
  { value: 'trainer', label: 'Docente / Formatore' },
]

const STATUS: Record<RowStatus, { label: string; variant: 'success' | 'default' | 'info' | 'danger' }> = {
  new: { label: 'Nuovo', variant: 'success' },
  existing: { label: 'Già presente', variant: 'default' },
  update: { label: 'Da aggiornare', variant: 'info' },
  error: { label: 'Errore', variant: 'danger' },
}

export default function AdminUsersImportPage() {
  const [courses, setCourses] = useState<AdminCourse[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [role, setRole] = useState('member')
  const [courseIds, setCourseIds] = useState<number[]>([])
  const [updateExisting, setUpdateExisting] = useState(false)
  const [check, setCheck] = useState<ImportResult | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [working, setWorking] = useState<'check' | 'import' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [onlyProblems, setOnlyProblems] = useState(false)

  useEffect(() => {
    lmsApi.admin.getCourses().then(setCourses).catch(() => undefined)
  }, [])

  // Any change to file or options invalidates the previous check
  useEffect(() => {
    setCheck(null)
  }, [file, role, courseIds, updateExisting])

  const downloadTemplate = async (type: 'xlsx' | 'csv') => {
    try {
      const response = await apiClient.get(`/admin/users/import-template/?tipo=${type}`, { responseType: 'blob' })
      saveBlob(response.data, `modello_import_utenti.${type}`)
    } catch (err) {
      setError(await apiErrorMessage(err, 'Impossibile scaricare il modello.'))
    }
  }

  const send = async (dryRun: boolean): Promise<ImportResult> => {
    const formData = new FormData()
    formData.append('file', file!)
    formData.append('dry_run', dryRun ? 'true' : 'false')
    formData.append('role', role)
    formData.append('update_existing', updateExisting ? 'true' : 'false')
    courseIds.forEach((id) => formData.append('course_ids', String(id)))
    const response = await apiClient.post('/admin/users/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  const handleCheck = async () => {
    if (!file) return
    setWorking('check')
    setError(null)
    setResult(null)
    try {
      setCheck(await send(true))
    } catch (err) {
      setError(await apiErrorMessage(err, 'Impossibile leggere il file.'))
    } finally {
      setWorking(null)
    }
  }

  const handleImport = async () => {
    setWorking('import')
    setError(null)
    try {
      const data = await send(false)
      setResult(data)
      setCheck(null)
      setFile(null)
    } catch (err) {
      setError(await apiErrorMessage(err, "Errore durante l'import."))
    } finally {
      setWorking(null)
    }
  }

  const downloadCredentials = (type: 'xlsx' | 'csv') => {
    if (!result?.credentials?.length) return
    const stamp = new Date().toISOString().slice(0, 10)
    if (type === 'xlsx' && result.credentials_xlsx) {
      const bytes = Uint8Array.from(atob(result.credentials_xlsx), (c) => c.charCodeAt(0))
      saveBlob(
        new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `credenziali_utenti_${stamp}.xlsx`
      )
      return
    }
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`
    const lines = [['Nome', 'Cognome', 'Email (nome utente)', 'Password']]
      .concat(result.credentials.map((c) => [c.first_name, c.last_name, c.email, c.password]))
      .map((row) => row.map(escape).join(';'))
    saveBlob(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }), `credenziali_utenti_${stamp}.csv`)
  }

  const toggleCourse = (id: number) =>
    setCourseIds((current) => (current.includes(id) ? current.filter((c) => c !== id) : [...current, id]))

  const toImport = check ? check.summary.new + check.summary.update + (courseIds.length ? check.summary.existing : 0) : 0
  const rows = (check?.rows || []).filter((row) => !onlyProblems || row.errors.length || row.warnings.length)

  return (
    <div className="space-y-6">
      <div>
        <Link to="/gestione/users" className="inline-flex items-center text-sm text-gray-500 hover:text-primary-600 mb-1">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Gestione utenti
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Importa utenti</h1>
        <p className="text-gray-600">
          Crea gli account da un elenco Excel o CSV (ad esempio gli alunni forniti da una scuola), senza registrazione da parte degli utenti.
        </p>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-4 py-3">{error}</p>}

      {result && (
        <Card className="p-6 border-green-200">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
            <div className="space-y-3 w-full">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Import completato</h2>
                <p className="text-gray-600">
                  {result.created} utenti creati
                  {result.summary.update > 0 && `, ${result.summary.update} aggiornati`}
                  {result.enrollments ? `, ${result.enrollments} iscrizioni ai corsi` : ''}
                  {result.summary.error > 0 && ` · ${result.summary.error} righe con errori non importate`}.
                </p>
              </div>
              {!!result.credentials?.length && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-sm text-amber-900 font-medium flex items-center">
                    <KeyIcon className="h-5 w-5 mr-2" />
                    Scarica ora le credenziali: le password non verranno più mostrate.
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Il file contiene le password in chiaro: consegnalo alla scuola in modo sicuro e poi eliminalo.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm" onClick={() => downloadCredentials('xlsx')}>
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Credenziali (Excel)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadCredentials('csv')}>
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Credenziali (CSV)
                    </Button>
                  </div>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => setResult(null)}>
                Importa un altro file
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!result && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card className="xl:col-span-2">
            <CardHeader title="1. Carica l'elenco" />
            <div className="space-y-5">
              <div>
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="mt-1 text-xs text-gray-500">
                  File Excel (.xlsx) o CSV, prima riga con le intestazioni, un utente per riga (massimo 5.000).
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo dei nuovi utenti</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {ROLES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-start gap-2 sm:mt-7">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    Aggiorna i dati degli utenti già presenti (stessa email); le loro password non cambiano
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Iscrivi ai corsi (facoltativo)</label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {courses.map((course) => (
                    <label key={course.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={courseIds.includes(course.id)}
                        onChange={() => toggleCourse(course.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>{course.title}</span>
                      {!course.is_published && <Badge size="sm">Non pubblicato</Badge>}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Vengono iscritti sia i nuovi utenti sia quelli già presenti nell'elenco.
                </p>
              </div>

              <Button onClick={handleCheck} disabled={!file || working !== null}>
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                {working === 'check' ? 'Verifica in corso...' : 'Verifica il file'}
              </Button>
            </div>
          </Card>

          <Card>
            <CardHeader title="Modello" />
            <p className="text-sm text-gray-600 mb-3">
              Parti dal modello: le intestazioni possono essere anche scritte in modo leggermente diverso
              (es. "E-mail", "CF", "Classe").
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button size="sm" variant="outline" onClick={() => downloadTemplate('xlsx')}>
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Modello Excel
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadTemplate('csv')}>
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Modello CSV
              </Button>
            </div>
            <ul className="divide-y divide-gray-100 text-sm">
              {COLUMNS.map(([name, help]) => (
                <li key={name} className="py-1.5">
                  <span className="font-medium text-gray-900">{name}</span>
                  <span className="block text-xs text-gray-500">{help}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {working === 'check' && <InlineSpinner message="Controllo delle righe..." />}

      {check && !result && (
        <Card>
          <CardHeader title="2. Controlla e importa" />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="success">{check.summary.new} nuovi</Badge>
            <Badge variant="default">{check.summary.existing} già presenti</Badge>
            {check.summary.update > 0 && <Badge variant="info">{check.summary.update} da aggiornare</Badge>}
            <Badge variant={check.summary.error ? 'danger' : 'default'}>{check.summary.error} con errori</Badge>
            <span className="text-xs text-gray-500 ml-2">Colonne riconosciute: {check.columns.join(', ')}</span>
          </div>

          {check.summary.error > 0 && (
            <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2 mb-4 flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              Le righe con errori non verranno importate: correggile nel file e ricaricalo, oppure importa ora le altre.
            </p>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
            <input
              type="checkbox"
              checked={onlyProblems}
              onChange={(e) => setOnlyProblems(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Mostra solo le righe con errori o avvisi
          </label>
          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-gray-500">
                  <th className="py-2 px-3 font-medium">Riga</th>
                  <th className="py-2 px-3 font-medium">Esito</th>
                  <th className="py-2 px-3 font-medium">Utente</th>
                  <th className="py-2 px-3 font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.row}>
                    <td className="py-2 px-3 text-gray-500">{row.row}</td>
                    <td className="py-2 px-3">
                      <Badge variant={STATUS[row.status].variant} size="sm">{STATUS[row.status].label}</Badge>
                    </td>
                    <td className="py-2 px-3">
                      <span className="font-medium text-gray-900">{row.first_name} {row.last_name}</span>
                      <span className="block text-gray-500">{row.email || '(email mancante)'}</span>
                    </td>
                    <td className="py-2 px-3">
                      {row.errors.map((message) => (
                        <span key={message} className="block text-red-700">{message}</span>
                      ))}
                      {row.warnings.map((message) => (
                        <span key={message} className="block text-amber-700">{message}</span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={handleImport} disabled={toImport === 0 || working !== null}>
              {working === 'import'
                ? 'Import in corso...'
                : `Importa ${check.summary.new} nuovi${check.summary.update ? ` e aggiorna ${check.summary.update}` : ''}`}
            </Button>
            {toImport === 0 && <span className="text-sm text-gray-500 self-center">Non c'è nulla da importare.</span>}
          </div>
        </Card>
      )}
    </div>
  )
}
